import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

// V2 badges are tinted by default (badge.css: solid is opt-out, not the
// default) — every colour variant here pairs a *-subtle background with the
// matching *-text ink, never a solid fill + white text. info/success/warning/
// neutral map onto the same four hues the mockup's status pills use
// (cyan/lime/amber/gray), pulled straight from tokens.css's existing
// semantic info/success/warning/neutral pairs.
const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-etn-pill border border-transparent px-2.5 py-0.5 text-etn-micro font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-etn-accent-tint-hover",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-etn-control-subtle-hover",
        destructive:
          "bg-etn-danger-subtle text-etn-danger-text [a&]:hover:bg-etn-danger-subtle-hover focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border-etn-border-hairline text-etn-text-cell [a&]:hover:bg-etn-control-subtle",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-etn-accent underline-offset-4 [a&]:hover:underline",
        info: "bg-etn-info-subtle text-etn-info-text [a&]:hover:bg-etn-info-subtle",
        success: "bg-etn-success-subtle text-etn-success-text [a&]:hover:bg-etn-success-subtle",
        warning: "bg-etn-warning-subtle text-etn-warning-text [a&]:hover:bg-etn-warning-subtle",
        neutral: "bg-etn-neutral-subtle text-etn-neutral-text [a&]:hover:bg-etn-neutral-subtle",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
